import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';
import { Search, Plus, User, Phone, Mail, Tag, Clock, ChevronRight, Building2, X, Trash2, Edit3, Upload, Download, CheckSquare, Square, Users, PhoneOutgoing, MessageSquare } from 'lucide-react';
import { Checkbox } from './ui/checkbox';
import ContactChat from './ContactChat';
import axios from 'axios';
import Logo from './Logo';

import { BACKEND_URL } from "../lib/backendUrl";
const API = `${BACKEND_URL}/api`;

const STATUS_CONFIG = {
  new: { label: 'Leads', color: 'bg-blue-500/15 text-blue-400 border-blue-500/30' },
  qualified: { label: 'Qualified', color: 'bg-amber-500/15 text-amber-400 border-amber-500/30' },
  contacted: { label: 'Contacted', color: 'bg-purple-500/15 text-purple-400 border-purple-500/30' },
  customer: { label: 'Customer', color: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' },
  lost: { label: 'Lost', color: 'bg-red-500/15 text-red-400 border-red-500/30' },
};

const CRM = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedContact, setSelectedContact] = useState(null);
  const [contactDetail, setContactDetail] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [chatContact, setChatContact] = useState(null);
  const [chatChannel, setChatChannel] = useState('email');

  const [form, setForm] = useState({ name: '', phone: '', email: '', company: '', notes: '', service_interested: '', status: 'new', tags: [] });
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [allTags, setAllTags] = useState([]);
  const [tagFilter, setTagFilter] = useState('');
  const [newTag, setNewTag] = useState('');
  const [showBulkTag, setShowBulkTag] = useState(false);
  const [bulkTagInput, setBulkTagInput] = useState('');
  const [smartLists, setSmartLists] = useState([]);
  const [showCreateList, setShowCreateList] = useState(false);
  const [listForm, setListForm] = useState({ name: '', filters: { tag: '', status: '', source: '' } });
  const [browsingList, setBrowsingList] = useState(null);
  const [browsingContacts, setBrowsingContacts] = useState([]);

  useEffect(() => { fetchContacts(); fetchStats(); fetchTags(); fetchSmartLists(); }, [token, statusFilter, tagFilter]);

  // Deep link: /crm?contact=<id> or /crm?phone=<number> opens that contact's detail
  const [searchParams] = useSearchParams();
  const deepLinkHandled = useRef(false);
  useEffect(() => {
    if (deepLinkHandled.current || loading) return;
    const cid = searchParams.get('contact');
    const phone = searchParams.get('phone');
    if (cid) {
      deepLinkHandled.current = true;
      fetchContactDetail(cid);
    } else if (phone) {
      deepLinkHandled.current = true;
      const digits = phone.replace(/\D/g, '').slice(-10);
      const match = contacts.find(c => (c.phone || '').replace(/\D/g, '').slice(-10) === digits && digits);
      if (match) fetchContactDetail(match.id);
      else toast({ title: 'No CRM contact found for this caller', description: phone });
    }
  }, [contacts, loading]);

  const fetchTags = async () => {
    try {
      const res = await axios.get(`${API}/crm/tags`, { headers: { Authorization: `Bearer ${token}` } });
      setAllTags(res.data);
    } catch {}
  };

  const fetchContacts = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.set('status_filter', statusFilter);
      if (tagFilter) params.set('tag_filter', tagFilter);
      if (search) params.set('search', search);
      const res = await axios.get(`${API}/crm/contacts?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContacts(res.data);
    } catch (err) {
      console.error('Failed to fetch contacts:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/crm/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(res.data);
    } catch (err) { /* ignore */ }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}/crm/import`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      toast({ title: `Imported ${res.data.imported} contacts (${res.data.skipped} skipped)` });
      fetchContacts();
      fetchStats();
    } catch (err) {
      toast({ title: 'Import failed', description: err.response?.data?.detail, variant: 'destructive' });
    }
    e.target.value = '';
  };

  const fetchContactDetail = async (id) => {
    try {
      const res = await axios.get(`${API}/crm/contacts/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContactDetail(res.data);
      setSelectedContact(id);
    } catch (err) {
      toast({ title: 'Failed to load contact', variant: 'destructive' });
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchContacts();
  };

  const handleAddContact = async () => {
    if (!form.name.trim()) {
      toast({ title: 'Name is required', variant: 'destructive' });
      return;
    }
    try {
      await axios.post(`${API}/crm/contacts`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Contact added!' });
      setShowAddForm(false);
      setForm({ name: '', phone: '', email: '', company: '', notes: '', service_interested: '', status: 'new' });
      fetchContacts();
      fetchStats();
    } catch (err) {
      toast({ title: 'Failed to add contact', variant: 'destructive' });
    }
  };

  const handleUpdateContact = async () => {
    try {
      await axios.put(`${API}/crm/contacts/${selectedContact}`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Contact updated!' });
      setEditMode(false);
      fetchContactDetail(selectedContact);
      fetchContacts();
      fetchStats();
    } catch (err) {
      toast({ title: 'Failed to update contact', variant: 'destructive' });
    }
  };

  const handleDeleteContact = async (id) => {
    try {
      await axios.delete(`${API}/crm/contacts/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Contact deleted' });
      setSelectedContact(null);
      setContactDetail(null);
      fetchContacts();
      fetchStats();
    } catch (err) {
      toast({ title: 'Failed to delete contact', variant: 'destructive' });
    }
  };

  const handleStatusChange = async (contactId, newStatus) => {
    try {
      await axios.put(`${API}/crm/contacts/${contactId}`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchContacts();
      fetchStats();
      if (contactDetail?.contact?.id === contactId) {
        fetchContactDetail(contactId);
      }
    } catch (err) { /* ignore */ }
  };

  const startEdit = (contact) => {
    setForm({
      name: contact.name || '',
      phone: contact.phone || '',
      email: contact.email || '',
      company: contact.company || '',
      notes: contact.notes || '',
      service_interested: contact.service_interested || '',
      status: contact.status || 'new',
    });
    setEditMode(true);
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === contacts.length && contacts.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(contacts.map(c => c.id)));
    }
  };

  const handleExport = async () => {
    try {
      const params = selectedIds.size > 0 ? `?ids=${[...selectedIds].join(',')}` : '';
      const res = await axios.get(`${API}/crm/export${params}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `contacts_export_${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast({ title: selectedIds.size > 0 ? `Exported ${selectedIds.size} contacts` : 'All contacts exported' });
    } catch (err) {
      toast({ title: 'Export failed', variant: 'destructive' });
    }
  };

  const handleBulkTag = async (action) => {
    if (!bulkTagInput.trim() || selectedIds.size === 0) return;
    try {
      await axios.post(`${API}/crm/bulk-tag`, { contact_ids: [...selectedIds], tag: bulkTagInput.trim(), action }, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: `Tag "${bulkTagInput}" ${action === 'remove' ? 'removed from' : 'added to'} ${selectedIds.size} contacts` });
      setBulkTagInput(''); setShowBulkTag(false); setSelectedIds(new Set());
      fetchContacts(); fetchTags();
    } catch { toast({ title: 'Failed', variant: 'destructive' }); }
  };

  const addTagToContact = async (contactId, tag) => {
    if (!tag.trim()) return;
    try {
      await axios.post(`${API}/crm/bulk-tag`, { contact_ids: [contactId], tag: tag.trim(), action: 'add' }, { headers: { Authorization: `Bearer ${token}` } });
      fetchContacts(); fetchTags();
      if (contactDetail?.contact?.id === contactId) fetchContactDetail(contactId);
    } catch {}
  };

  const removeTagFromContact = async (contactId, tag) => {
    try {
      await axios.post(`${API}/crm/bulk-tag`, { contact_ids: [contactId], tag, action: 'remove' }, { headers: { Authorization: `Bearer ${token}` } });
      fetchContacts(); fetchTags();
      if (contactDetail?.contact?.id === contactId) fetchContactDetail(contactId);
    } catch {}
  };

  const fetchSmartLists = async () => {
    try {
      const res = await axios.get(`${API}/crm/smart-lists`, { headers: { Authorization: `Bearer ${token}` } });
      setSmartLists(res.data);
    } catch {}
  };

  const createSmartList = async () => {
    if (!listForm.name.trim()) { toast({ title: 'Name required', variant: 'destructive' }); return; }
    const filters = {};
    if (listForm.filters.tag) filters.tag = listForm.filters.tag;
    if (listForm.filters.status) filters.status = listForm.filters.status;
    if (listForm.filters.source) filters.source = listForm.filters.source;
    try {
      await axios.post(`${API}/crm/smart-lists`, { name: listForm.name, filters }, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Smart List created' });
      setShowCreateList(false); setListForm({ name: '', filters: { tag: '', status: '', source: '' } });
      fetchSmartLists();
    } catch { toast({ title: 'Failed', variant: 'destructive' }); }
  };

  const browseSmartList = async (listId) => {
    try {
      const res = await axios.get(`${API}/crm/smart-lists/${listId}`, { headers: { Authorization: `Bearer ${token}` } });
      setBrowsingList(res.data);
      setBrowsingContacts(res.data.contacts || []);
    } catch { toast({ title: 'Failed to load', variant: 'destructive' }); }
  };

  const deleteSmartList = async (listId) => {
    try {
      await axios.delete(`${API}/crm/smart-lists/${listId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Smart List deleted' });
      if (browsingList?.id === listId) { setBrowsingList(null); setBrowsingContacts([]); }
      fetchSmartLists();
    } catch { toast({ title: 'Failed', variant: 'destructive' }); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a1628] via-[#0d1f35] to-[#0a1628]" data-testid="crm-page">
      <header className="border-b border-white/5 bg-[#0a1628]/80 backdrop-blur-lg">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Logo linkTo="/dashboard" />
            <div className="h-6 w-px bg-white/10" />
            <h1 className="text-xl font-bold text-white">CRM</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleExport} className="border-white/10 text-gray-300 hover:bg-white/5" data-testid="export-btn">
              <Download className="w-4 h-4 mr-2" /> {selectedIds.size > 0 ? `Export (${selectedIds.size})` : 'Export All'}
            </Button>
            <label className="cursor-pointer">
              <input type="file" accept=".csv,.xlsx,.xls" onChange={handleImport} className="hidden" data-testid="import-file-input" />
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium border border-white/10 text-gray-300 hover:bg-white/5 transition-colors">
                <Upload className="w-4 h-4" /> Import CSV
              </span>
            </label>
            <Button onClick={() => { setShowAddForm(true); setForm({ name: '', phone: '', email: '', company: '', notes: '', service_interested: '', status: 'new' }); }} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="add-contact-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Contact
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Pipeline Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 mb-6" data-testid="crm-pipeline">
          {[
            { key: 'all', label: 'All', value: stats.total || 0, c: 'bg-white/5 text-white border-white/10' },
            ...Object.entries(STATUS_CONFIG).map(([k, v]) => ({ key: k, label: v.label, value: stats[k] || 0, c: v.color }))
          ].map(s => (
            <button
              key={s.key}
              onClick={() => setStatusFilter(s.key)}
              className={`p-3 rounded-lg border text-center transition-all ${statusFilter === s.key ? 'ring-1 ring-purple-500 scale-[1.02]' : 'hover:scale-[1.01]'} ${s.c}`}
              data-testid={`filter-${s.key}`}
            >
              <div className="text-2xl font-bold">{s.value}</div>
              <div className="text-xs opacity-70">{s.label}</div>
            </button>
          ))}
        </div>

        {/* Search */}
        <form onSubmit={handleSearch} className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search contacts by name, phone, email..."
              className="bg-gray-900/50 border-white/10 text-white pl-10 h-11"
              data-testid="crm-search"
            />
          </div>
        </form>

        {/* Tag Filter + Segmentation */}
        {allTags.length > 0 && (
          <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="tag-filters">
            <Tag className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
            <button onClick={() => setTagFilter('')} className={`text-xs px-2.5 py-1 rounded-full transition-all ${!tagFilter ? 'bg-purple-600 text-white' : 'bg-white/[0.03] text-gray-400 hover:text-white border border-white/[0.06]'}`} data-testid="tag-all">All</button>
            {allTags.map(tag => (
              <button key={tag} onClick={() => setTagFilter(tagFilter === tag ? '' : tag)} data-testid={`tag-filter-${tag}`}
                className={`text-xs px-2.5 py-1 rounded-full transition-all ${tagFilter === tag ? 'bg-purple-600 text-white' : 'bg-white/[0.03] text-gray-400 hover:text-white border border-white/[0.06]'}`}>
                {tag}
              </button>
            ))}
          </div>
        )}

        {/* Bulk Tag Actions */}
        {selectedIds.size > 0 && (
          <div className="flex items-center gap-2 mb-4 p-3 rounded-lg bg-purple-500/5 border border-purple-500/20" data-testid="bulk-tag-bar">
            <Tag className="w-4 h-4 text-purple-400 flex-shrink-0" />
            <span className="text-xs text-purple-400">{selectedIds.size} selected</span>
            {showBulkTag ? (
              <div className="flex items-center gap-2 ml-2">
                <Input value={bulkTagInput} onChange={e => setBulkTagInput(e.target.value)} placeholder="Enter tag..." className="bg-black/30 border-white/10 text-white h-7 text-xs w-32" data-testid="bulk-tag-input" />
                <Button size="sm" onClick={() => handleBulkTag('add')} className="bg-purple-600 hover:bg-purple-700 text-white text-xs h-7 px-2" data-testid="bulk-tag-add">+ Add Tag</Button>
                <Button size="sm" onClick={() => handleBulkTag('remove')} variant="outline" className="border-white/10 text-gray-400 text-xs h-7 px-2" data-testid="bulk-tag-remove">Remove</Button>
                <button onClick={() => setShowBulkTag(false)} className="text-gray-500 hover:text-white"><X className="w-3.5 h-3.5" /></button>
              </div>
            ) : (
              <Button size="sm" variant="ghost" onClick={() => setShowBulkTag(true)} className="text-purple-400 hover:text-purple-300 text-xs ml-2" data-testid="show-bulk-tag">
                <Tag className="w-3 h-3 mr-1" /> Tag Selected
              </Button>
            )}
          </div>
        )}

        {/* Smart Lists */}
        <div className="mb-4" data-testid="smart-lists-section">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Smart Lists</h3>
              <Button size="sm" variant="ghost" onClick={() => setShowCreateList(!showCreateList)} className="text-purple-400 hover:text-purple-300 text-xs h-6 px-2" data-testid="create-smart-list-toggle">
                {showCreateList ? <X className="w-3 h-3" /> : <Plus className="w-3 h-3 mr-1" />} {showCreateList ? '' : 'New List'}
              </Button>
            </div>
            {showCreateList && (
              <Card className="bg-white/[0.03] border-white/[0.06] mb-3" data-testid="create-smart-list-form">
                <CardContent className="pt-4 space-y-2">
                  <Input value={listForm.name} onChange={e => setListForm(f => ({...f, name: e.target.value}))} placeholder="List name (e.g., NYC VIP Leads)" className="bg-black/30 border-white/10 text-white h-8 text-xs" data-testid="smart-list-name" />
                  <div className="grid grid-cols-3 gap-2">
                    <select value={listForm.filters.tag} onChange={e => setListForm(f => ({...f, filters: {...f.filters, tag: e.target.value}}))} className="bg-black/30 border border-white/10 text-gray-300 text-xs rounded px-2 py-1.5 outline-none" data-testid="smart-list-tag">
                      <option value="">Any Tag</option>
                      {allTags.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <select value={listForm.filters.status} onChange={e => setListForm(f => ({...f, filters: {...f.filters, status: e.target.value}}))} className="bg-black/30 border border-white/10 text-gray-300 text-xs rounded px-2 py-1.5 outline-none" data-testid="smart-list-status">
                      <option value="">Any Status</option>
                      {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                    </select>
                    <select value={listForm.filters.source} onChange={e => setListForm(f => ({...f, filters: {...f.filters, source: e.target.value}}))} className="bg-black/30 border border-white/10 text-gray-300 text-xs rounded px-2 py-1.5 outline-none" data-testid="smart-list-source">
                      <option value="">Any Source</option>
                      <option value="phone_call">Phone Call</option>
                      <option value="chat_widget">Chat Widget</option>
                      <option value="manual">Manual</option>
                      <option value="import">Import</option>
                    </select>
                  </div>
                  <Button size="sm" onClick={createSmartList} className="bg-purple-600 hover:bg-purple-700 text-white text-xs w-full" data-testid="save-smart-list">Create Smart List</Button>
                </CardContent>
              </Card>
            )}
            <div className="flex gap-2 flex-wrap">
              {smartLists.map(sl => (
                <div key={sl.id} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs cursor-pointer transition-all ${browsingList?.id === sl.id ? 'bg-purple-600/20 border-purple-500/30 text-purple-400' : 'bg-white/[0.03] border-white/[0.06] text-gray-400 hover:border-white/10'}`} data-testid={`smart-list-${sl.id}`}>
                  <button onClick={() => browseSmartList(sl.id)} className="flex items-center gap-1.5">
                    <Users className="w-3 h-3" /> {sl.name}
                    <span className="text-[10px] text-gray-500 ml-1">({sl.contact_count})</span>
                  </button>
                  <button onClick={() => deleteSmartList(sl.id)} className="text-gray-600 hover:text-red-400 ml-1"><X className="w-3 h-3" /></button>
                </div>
              ))}
              {smartLists.length === 0 && !showCreateList && (
                <button onClick={() => setShowCreateList(true)} className="text-xs text-gray-500 hover:text-purple-400 flex items-center gap-1 px-3 py-1.5 rounded-lg border border-dashed border-white/10 hover:border-purple-500/30">
                  <Plus className="w-3 h-3" /> Create your first Smart List
                </button>
              )}
            </div>
          </div>

        {/* Smart List Browse View */}
        {browsingList && (
          <Card className="bg-white/[0.03] border-purple-500/20 mb-4" data-testid="smart-list-browse">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="text-sm font-medium text-white flex items-center gap-2">
                    <Users className="w-4 h-4 text-purple-400" /> {browsingList.name}
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-500/10 text-purple-400 border border-purple-500/20">{browsingContacts.length} contacts</span>
                  </h3>
                  <div className="flex gap-1.5 mt-1">
                    {browsingList.filters?.tag && <span className="text-[9px] px-1.5 py-0.5 rounded bg-purple-500/10 text-purple-400">Tag: {browsingList.filters.tag}</span>}
                    {browsingList.filters?.status && <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400">Status: {browsingList.filters.status}</span>}
                    {browsingList.filters?.source && <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400">Source: {browsingList.filters.source}</span>}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => navigate(`/outbound-calls?smart_list=${browsingList.id}`)} className="bg-purple-600 hover:bg-purple-700 text-white text-xs" data-testid="use-in-campaign">
                    <PhoneOutgoing className="w-3 h-3 mr-1" /> Use in Campaign
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => { setBrowsingList(null); setBrowsingContacts([]); }} className="text-gray-400 h-7 w-7 p-0"><X className="w-4 h-4" /></Button>
                </div>
              </div>
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {browsingContacts.map(c => (
                  <div key={c.id} className="flex items-center gap-3 p-2 rounded bg-white/[0.02] text-xs">
                    <div className="w-6 h-6 rounded-full bg-purple-600/20 flex items-center justify-center text-purple-400 text-[10px] font-bold flex-shrink-0">{(c.name || '?')[0].toUpperCase()}</div>
                    <span className="text-white font-medium flex-1 truncate">{c.name || '—'}</span>
                    <span className="text-gray-500">{c.phone || c.email || ''}</span>
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full border ${STATUS_CONFIG[c.status]?.color || 'bg-gray-500/15 text-gray-400 border-gray-500/30'}`}>{STATUS_CONFIG[c.status]?.label || c.status}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Contact List */}
          <div className="lg:col-span-2 space-y-2" data-testid="contact-list">
            {/* Select All Bar */}
            {contacts.length > 0 && !loading && (
              <div className="flex items-center gap-3 px-4 py-2 rounded-lg bg-white/[0.02] border border-white/[0.06]" data-testid="select-all-bar">
                <Checkbox
                  checked={selectedIds.size === contacts.length && contacts.length > 0}
                  onCheckedChange={toggleSelectAll}
                  data-testid="select-all-checkbox"
                  className="border-white/20 data-[state=checked]:bg-purple-600 data-[state=checked]:border-purple-600"
                />
                <span className="text-xs text-gray-400">
                  {selectedIds.size > 0 ? `${selectedIds.size} of ${contacts.length} selected` : `Select all (${contacts.length})`}
                </span>
                {selectedIds.size > 0 && (
                  <button onClick={() => setSelectedIds(new Set())} className="text-xs text-gray-500 hover:text-white ml-auto">Clear</button>
                )}
              </div>
            )}
            {loading ? (
              <div className="text-center py-16 text-gray-400">Loading contacts...</div>
            ) : contacts.length === 0 ? (
              <Card className="bg-gray-900/50 border-white/10">
                <CardContent className="py-16 text-center">
                  <User className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2" data-testid="no-contacts-msg">No contacts yet</h3>
                  <p className="text-gray-400 mb-4">Contacts are automatically created when your AI receptionist handles calls.</p>
                  <Button onClick={() => setShowAddForm(true)} className="bg-purple-600 hover:bg-purple-700 text-white">
                    <Plus className="w-4 h-4 mr-2" /> Add First Contact
                  </Button>
                </CardContent>
              </Card>
            ) : (
              contacts.map(c => {
                const st = STATUS_CONFIG[c.status] || STATUS_CONFIG.new;
                return (
                  <div
                    key={c.id}
                    onClick={() => fetchContactDetail(c.id)}
                    className={`p-4 rounded-lg border cursor-pointer transition-all flex items-center gap-4 ${selectedContact === c.id ? 'bg-purple-500/10 border-purple-500/30' : 'bg-gray-900/50 border-white/10 hover:border-white/20'}`}
                    data-testid={`contact-row-${c.id}`}
                  >
                    <Checkbox
                      checked={selectedIds.has(c.id)}
                      onCheckedChange={(e) => { toggleSelect(c.id); }}
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`checkbox-${c.id}`}
                      className="border-white/20 data-[state=checked]:bg-purple-600 data-[state=checked]:border-purple-600 flex-shrink-0"
                    />
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-semibold text-sm">{(c.name || '?')[0].toUpperCase()}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-white font-medium truncate">{c.name || 'Unknown'}</p>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full border ${st.color}`}>{st.label}</span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                        {c.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{c.phone}</span>}
                        {c.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{c.email}</span>}
                        {c.call_count > 0 && <span>{c.call_count} call{c.call_count > 1 ? 's' : ''}</span>}
                      </div>
                      {(c.tags || []).length > 0 && (
                        <div className="flex gap-1 mt-1 flex-wrap">
                          {c.tags.map(tag => (
                            <span key={tag} className="text-[9px] px-1.5 py-0.5 rounded bg-purple-500/10 text-purple-400 border border-purple-500/20">{tag}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!c.email && !c.phone) { toast({ title: 'No email or phone on this contact', description: 'Add an email or phone number to start a conversation.', variant: 'destructive' }); return; }
                        setChatChannel(c.email ? 'email' : 'sms');
                        setChatContact(c);
                      }}
                      className={`p-2 rounded-lg transition-colors flex-shrink-0 ${(c.email || c.phone) ? 'text-purple-400 hover:bg-purple-500/15' : 'text-gray-700 cursor-not-allowed'}`}
                      title={(c.email || c.phone) ? 'Message this contact (email or SMS)' : 'No email or phone on this contact'}
                      data-testid={`chat-contact-${c.id}`}
                    >
                      <MessageSquare className="w-4 h-4" />
                    </button>
                    <ChevronRight className="w-4 h-4 text-gray-600 flex-shrink-0" />
                  </div>
                );
              })
            )}
          </div>

          {/* Contact Detail Panel */}
          <div className="lg:col-span-1">
            {contactDetail && !editMode ? (
              <Card className="bg-gray-900/50 border-white/10 sticky top-24" data-testid="contact-detail">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-white text-lg">{contactDetail.contact.name || 'Unknown'}</CardTitle>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => startEdit(contactDetail.contact)} className="text-gray-400 hover:text-white h-8 w-8 p-0">
                        <Edit3 className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDeleteContact(contactDetail.contact.id)} className="text-gray-400 hover:text-red-400 h-8 w-8 p-0" data-testid="delete-contact-btn">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Status selector */}
                  <Select value={contactDetail.contact.status} onValueChange={(v) => handleStatusChange(contactDetail.contact.id, v)}>
                    <SelectTrigger className="bg-black/30 border-white/10 text-white h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-900 border-white/10">
                      {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                        <SelectItem key={k} value={k} className="text-white">{v.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Contact info */}
                  <div className="space-y-2 text-sm">
                    {contactDetail.contact.phone && (
                      <div className="flex items-center gap-2 text-gray-300">
                        <Phone className="w-4 h-4 text-gray-500" /> {contactDetail.contact.phone}
                        <button
                          onClick={() => {
                            const phone = contactDetail.contact.phone.startsWith('+') ? contactDetail.contact.phone : `+1${contactDetail.contact.phone.replace(/\D/g, '')}`;
                            navigate(`/outbound-calls?quickcall=1&phone=${encodeURIComponent(phone)}&name=${encodeURIComponent(contactDetail.contact.name || '')}`);
                          }}
                          className="ml-auto px-2 py-1 rounded bg-purple-600/20 text-purple-400 text-xs hover:bg-purple-600/30 transition-colors flex items-center gap-1"
                          data-testid="quick-call-contact"
                        >
                          <Phone className="w-3 h-3" /> Call
                        </button>
                        <button
                          onClick={() => { setChatChannel('sms'); setChatContact(contactDetail.contact); }}
                          className="px-2 py-1 rounded bg-cyan-600/20 text-cyan-400 text-xs hover:bg-cyan-600/30 transition-colors flex items-center gap-1"
                          data-testid="quick-sms-contact"
                        >
                          <MessageSquare className="w-3 h-3" /> SMS
                        </button>
                      </div>
                    )}
                    {contactDetail.contact.email && (
                      <div className="flex items-center gap-2 text-gray-300">
                        <Mail className="w-4 h-4 text-gray-500" /> {contactDetail.contact.email}
                        <button
                          onClick={() => { setChatChannel('email'); setChatContact(contactDetail.contact); }}
                          className="ml-auto px-2 py-1 rounded bg-purple-600/20 text-purple-400 text-xs hover:bg-purple-600/30 transition-colors flex items-center gap-1"
                          data-testid="quick-message-contact"
                        >
                          <MessageSquare className="w-3 h-3" /> Message
                        </button>
                      </div>
                    )}
                    {contactDetail.contact.company && (
                      <div className="flex items-center gap-2 text-gray-300">
                        <Building2 className="w-4 h-4 text-gray-500" /> {contactDetail.contact.company}
                      </div>
                    )}
                    {contactDetail.contact.service_interested && (
                      <div className="flex items-center gap-2 text-gray-300">
                        <Tag className="w-4 h-4 text-gray-500" /> {contactDetail.contact.service_interested}
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-gray-500 text-xs">
                      <Clock className="w-3 h-3" /> Source: {contactDetail.contact.source} | {contactDetail.contact.call_count} call(s)
                    </div>
                  </div>

                  {/* Notes */}
                  {contactDetail.contact.notes && (
                    <div className="p-3 bg-black/20 rounded-lg">
                      <p className="text-xs text-gray-500 mb-1">Notes</p>
                      <p className="text-sm text-gray-300 whitespace-pre-wrap">{contactDetail.contact.notes}</p>
                    </div>
                  )}

                  {/* Tags */}
                  <div data-testid="contact-tags">
                    <p className="text-xs text-gray-500 mb-2">Tags</p>
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {(contactDetail.contact.tags || []).map(tag => (
                        <span key={tag} className="text-[10px] px-2 py-0.5 rounded-full bg-purple-500/10 text-purple-400 border border-purple-500/20 flex items-center gap-1">
                          {tag}
                          <button onClick={() => removeTagFromContact(contactDetail.contact.id, tag)} className="hover:text-red-400 transition-colors" data-testid={`remove-tag-${tag}`}><X className="w-2.5 h-2.5" /></button>
                        </span>
                      ))}
                      {(contactDetail.contact.tags || []).length === 0 && <span className="text-[10px] text-gray-600">No tags</span>}
                    </div>
                    <div className="flex gap-1">
                      <Input
                        value={newTag}
                        onChange={e => setNewTag(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter' && newTag.trim()) { addTagToContact(contactDetail.contact.id, newTag); setNewTag(''); }}}
                        placeholder="Add tag..."
                        className="bg-black/30 border-white/10 text-white h-7 text-xs flex-1"
                        data-testid="add-tag-input"
                      />
                      <Button size="sm" onClick={() => { if (newTag.trim()) { addTagToContact(contactDetail.contact.id, newTag); setNewTag(''); }}} className="bg-purple-600 hover:bg-purple-700 text-white h-7 px-2 text-xs" data-testid="add-tag-btn">
                        <Plus className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>

                  {/* Call history */}
                  {contactDetail.calls?.length > 0 && (
                    <div>
                      <p className="text-xs text-gray-500 mb-2">Call History</p>
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {contactDetail.calls.map((call, i) => (
                          <div key={i} className="p-2 bg-black/20 rounded-lg text-xs">
                            <div className="flex justify-between text-gray-400">
                              <span>{call.intent || 'unknown'}</span>
                              <span>{call.created_at ? new Date(call.created_at).toLocaleDateString() : ''}</span>
                            </div>
                            {call.summary && <p className="text-gray-300 mt-1">{call.summary}</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : editMode ? (
              <Card className="bg-gray-900/50 border-white/10 sticky top-24" data-testid="edit-contact-form">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-white text-lg">Edit Contact</CardTitle>
                    <Button size="sm" variant="ghost" onClick={() => setEditMode(false)} className="text-gray-400 h-8 w-8 p-0"><X className="w-4 h-4" /></Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div><Label className="text-gray-400 text-xs">Name</Label><Input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} className="bg-black/30 border-white/10 text-white h-9" /></div>
                  <div><Label className="text-gray-400 text-xs">Phone</Label><Input value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} className="bg-black/30 border-white/10 text-white h-9" /></div>
                  <div><Label className="text-gray-400 text-xs">Email</Label><Input value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} className="bg-black/30 border-white/10 text-white h-9" /></div>
                  <div><Label className="text-gray-400 text-xs">Company</Label><Input value={form.company} onChange={e => setForm(f => ({...f, company: e.target.value}))} className="bg-black/30 border-white/10 text-white h-9" /></div>
                  <div><Label className="text-gray-400 text-xs">Interested In</Label><Input value={form.service_interested} onChange={e => setForm(f => ({...f, service_interested: e.target.value}))} className="bg-black/30 border-white/10 text-white h-9" /></div>
                  <div><Label className="text-gray-400 text-xs">Notes</Label><Textarea value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} className="bg-black/30 border-white/10 text-white min-h-[60px]" /></div>
                  <Button onClick={handleUpdateContact} className="w-full bg-purple-600 hover:bg-purple-700 text-white" data-testid="save-edit-btn">Save Changes</Button>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-gray-900/50 border-white/10">
                <CardContent className="py-12 text-center text-gray-500 text-sm">
                  Select a contact to view details
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>

      {/* Add Contact Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-6" data-testid="add-contact-modal">
          <Card className="bg-gray-900 border-white/10 w-full max-w-md">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white">Add Contact</CardTitle>
                <Button size="sm" variant="ghost" onClick={() => setShowAddForm(false)} className="text-gray-400 h-8 w-8 p-0"><X className="w-4 h-4" /></Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div><Label className="text-gray-400 text-xs">Name *</Label><Input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} placeholder="John Smith" className="bg-black/30 border-white/10 text-white" data-testid="add-contact-name" /></div>
              <div><Label className="text-gray-400 text-xs">Phone</Label><Input value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} placeholder="+1 555-123-4567" className="bg-black/30 border-white/10 text-white" /></div>
              <div><Label className="text-gray-400 text-xs">Email</Label><Input value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} placeholder="john@example.com" className="bg-black/30 border-white/10 text-white" /></div>
              <div><Label className="text-gray-400 text-xs">Company</Label><Input value={form.company} onChange={e => setForm(f => ({...f, company: e.target.value}))} className="bg-black/30 border-white/10 text-white" /></div>
              <div><Label className="text-gray-400 text-xs">Notes</Label><Textarea value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} className="bg-black/30 border-white/10 text-white min-h-[60px]" /></div>
              <Button onClick={handleAddContact} className="w-full bg-purple-600 hover:bg-purple-700 text-white" data-testid="submit-add-contact">Add Contact</Button>
            </CardContent>
          </Card>
        </div>
      )}

      {chatContact && (
        <ContactChat contact={chatContact} token={token} initialChannel={chatChannel} onClose={() => setChatContact(null)} />
      )}
    </div>
  );
};

export default CRM;
